import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { accountsApi, type AccountsListParams } from '../api/accounts';
import { ApiError } from '../api/client';
import type { Account, AccountInput, Page, UUID, WriteResult } from '../api/types';

export const accountsKeys = {
  all:    ['accounts'] as const,
  list:   (p: AccountsListParams) => ['accounts', 'list', p] as const,
  // Task #245 (2026-06-02): includeDeleted in cache key.
  detail: (id: UUID, includeDeleted = false) =>
    ['accounts', 'detail', id, includeDeleted ? 'with-deleted' : 'alive'] as const,
};

export function useAccounts(
  params: AccountsListParams,
  opts?: { enabled?: boolean },
) {
  return useQuery<Page<Account>, ApiError>({
    queryKey: accountsKeys.list(params),
    queryFn: () => accountsApi.list(params),
    // #39 (2026-06-11): zero pills → no fetch; page renders the hint.
    enabled: opts?.enabled ?? true,
    // #46 (2026-06-12): keep previous rows during filter-driven refetches.
    placeholderData: keepPreviousData,
  });
}

export function useAccount(id: UUID | undefined, includeDeleted = false) {
  return useQuery<Account, ApiError>({
    queryKey: accountsKeys.detail(id ?? '', includeDeleted),
    queryFn: () => accountsApi.get(id!, includeDeleted),
    enabled: !!id,
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Account>, ApiError, AccountInput>({
    mutationFn: accountsApi.create,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: accountsKeys.all });
      qc.invalidateQueries({ queryKey: ['compliance'] });
      qc.invalidateQueries({ queryKey: ['account-billing'] });  // a new related account adds a Fees-tab billing row
      if (!r.pending) qc.setQueryData(accountsKeys.detail(r.entity.id), r.entity);
    },
  });
}

export function useUpdateAccount(id: UUID) {
  const qc = useQueryClient();
  return useMutation<WriteResult<Account>, ApiError, AccountInput>({
    mutationFn: (p) => accountsApi.update(id, p),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: accountsKeys.all });
      qc.invalidateQueries({ queryKey: ['compliance'] });
      if (!r.pending) qc.setQueryData(accountsKeys.detail(id), r.entity);
    },
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Account>, ApiError, UUID>({
    mutationFn: accountsApi.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: accountsKeys.all }),
  });
}

export function useRestoreAccount() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Account>, ApiError, UUID>({
    mutationFn: accountsApi.restore,
    onSuccess: () => qc.invalidateQueries({ queryKey: accountsKeys.all }),
  });
}


// Beta #11 cascade extension (2026-05-26).
import type { ReferencesReport } from '../api/persons';

export function useAccountReferences(id: UUID | undefined, enabled = true) {
  return useQuery<ReferencesReport, ApiError>({
    queryKey: ['accounts', 'references', id ?? ''],
    queryFn: () => accountsApi.references(id!),
    enabled: enabled && !!id,
  });
}

interface CascadeDeleteAccountVars {
  id: UUID;
  refIds: Record<string, UUID[]>;
}

export function useCascadeDeleteAccount() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Account>, ApiError, CascadeDeleteAccountVars>({
    mutationFn: ({ id, refIds }) => accountsApi.cascadeDelete(id, refIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: accountsKeys.all });
      qc.invalidateQueries({ queryKey: ['account-parties'] });
      // ['insurance-policy-accounts'] dropped in #388 cleanup (2026-06-09):
      // dead query key — the cascade-delete affects Policies via
      // AccountInsurance.account_ids projection, busted by ['account-insurance'].
      qc.invalidateQueries({ queryKey: ['account-insurance'] });
      qc.invalidateQueries({ queryKey: ['compliance'] });
    },
  });
}


// A1 / #377 (2026-06-09): Detach hooks. Two flavours — one per starting
// state. Both produce orphan state (Inactive, both FKs NULL). Source-side
// caches (agreement / policy detail) are invalidated so the originating
// parent's "Accounts on this …" list refreshes after the detach commits.

export function useDetachAccountFromAgreement() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Account>, ApiError, UUID>({
    mutationFn: (id) => accountsApi.detachFromAgreement(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: accountsKeys.all });
      // Parent Agreement's Accounts tab + AccountInsuranceOut.account_ids
      // both derive from this. Invalidate broadly.
      qc.invalidateQueries({ queryKey: ['agreements'] });
      qc.invalidateQueries({ queryKey: ['account-insurance'] });
      qc.invalidateQueries({ queryKey: ['compliance'] });
      qc.invalidateQueries({ queryKey: ['account-billing'] });  // detach clears this account's billing (apply-path, C-214)
      if (!r.pending) qc.setQueryData(accountsKeys.detail(r.entity.id), r.entity);
    },
  });
}

/** #392 / #402 (2026-06-09): reattach an orphan Inactive Account to a
 *  target Agreement as Active Direct. Sibling of useDetachAccountFromAgreement —
 *  closes the orphan-lifecycle symmetry on the Account side. Invalidates
 *  the same caches as the detach side because the target Agreement's
 *  Accounts tab and any AccountInsuranceOut.account_ids projections need
 *  to refresh to reflect the new linkage. */
export function useAttachAccountToAgreement() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Account>, ApiError, { id: UUID; agreementId: UUID }>({
    mutationFn: ({ id, agreementId }) => accountsApi.attachToAgreement(id, agreementId),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: accountsKeys.all });
      qc.invalidateQueries({ queryKey: ['agreements'] });
      qc.invalidateQueries({ queryKey: ['account-insurance'] });
      qc.invalidateQueries({ queryKey: ['compliance'] });
      qc.invalidateQueries({ queryKey: ['account-billing'] });  // a re-attached account adds a Fees-tab billing row
      if (!r.pending) qc.setQueryData(accountsKeys.detail(r.entity.id), r.entity);
    },
  });
}

export function useDetachAccountFromPolicy() {
  const qc = useQueryClient();
  return useMutation<WriteResult<Account>, ApiError, UUID>({
    mutationFn: (id) => accountsApi.detachFromPolicy(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: accountsKeys.all });
      // Source Policy's `account_ids` list derives from this. Invalidate
      // account-insurance detail caches so the Policy's Accounts tab
      // refreshes after the detach commits.
      qc.invalidateQueries({ queryKey: ['account-insurance'] });
      qc.invalidateQueries({ queryKey: ['compliance'] });
      qc.invalidateQueries({ queryKey: ['account-billing'] });  // detach clears this account's billing (apply-path, C-214)
      if (!r.pending) qc.setQueryData(accountsKeys.detail(r.entity.id), r.entity);
    },
  });
}
